// =============================================================================
// BP-08-05: Vendor Portal Frontend - Types
// =============================================================================

import { z } from 'zod';

// =============================================================================
// Enums
// =============================================================================

export type VendorStatus = 'pending' | 'active' | 'suspended';
export type ProductStatus = 'draft' | 'active' | 'out_of_stock' | 'discontinued';
export type ProductPriceType = 'fixed' | 'range' | 'quote' | 'call';
export type OrderStatus = 
  | 'pending' 
  | 'confirmed' 
  | 'processing' 
  | 'shipped' 
  | 'delivered' 
  | 'cancelled' 
  | 'refunded';
export type ReviewStatus = 'pending' | 'approved' | 'rejected';

// =============================================================================
// Vendor Profile
// =============================================================================

export interface VendorAddress {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface VendorProfile {
  id: string;
  userId: string;
  name: string;
  slug: string;
  description: string;
  email: string;
  phone: string;
  website: string;
  address: VendorAddress;
  serviceAreaNational: boolean;
  serviceAreaStates: string[];
  logoUrl: string | null;
  bannerUrl: string | null;
  galleryUrls: string[];
  categoryIds: string[];
  isVerified: boolean;
  verifiedAt: string | null;
  verificationDocuments: VerificationDocument[];
  avgRating: number;
  reviewCount: number;
  yearsInBusiness: number;
  certifications: string[];
  insuranceAccepted: string[];
  status: VendorStatus;
  featuredUntil: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VerificationDocument {
  id: string;
  type: string;
  filename: string;
  uploadedAt: string;
  status: 'pending' | 'approved' | 'rejected';
  notes?: string;
}

// =============================================================================
// Products
// =============================================================================

export interface ProductSpecification {
  key: string;
  value: string;
  unit?: string;
}

export interface VendorProduct {
  id: string;
  vendorId: string;
  name: string;
  slug: string;
  description: string;
  shortDescription: string;
  categoryId: string;
  categoryName: string;
  priceMin: number | null;
  priceMax: number | null;
  priceType: ProductPriceType;
  acceptsInsurance: boolean;
  insuranceNotes: string;
  imageUrls: string[];
  specifications: ProductSpecification[];
  features: string[];
  diagnosisIds: string[];
  ageRangeMin: number | null;
  ageRangeMax: number | null;
  avgRating: number;
  reviewCount: number;
  status: ProductStatus;
  isFeatured: boolean;
  viewCount: number;
  inquiryCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProductListParams {
  page?: number;
  pageSize?: number;
  status?: ProductStatus | 'all';
  categoryId?: string;
  search?: string;
  sortBy?: 'name' | 'createdAt' | 'price' | 'rating' | 'views';
  sortOrder?: 'asc' | 'desc';
}

export interface ProductListResponse {
  items: VendorProduct[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// =============================================================================
// Orders
// =============================================================================

export interface OrderCustomer {
  familyId: string;
  familyName: string;
  contactName: string;
  email: string;
  phone: string;
}

export interface VendorOrder {
  id: string;
  orderNumber: string;
  customer: OrderCustomer;
  productId: string;
  productName: string;
  productImage: string | null;
  status: OrderStatus;
  description: string;
  quantity: number;
  subtotal: number;
  tax: number;
  shipping: number;
  total: number;
  shippingAddress: VendorAddress;
  trackingNumber: string | null;
  customerNotes: string;
  vendorNotes: string;
  insuranceClaimId: string | null;
  orderedAt: string;
  confirmedAt: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

export interface OrderListParams {
  page?: number;
  pageSize?: number;
  status?: OrderStatus | 'all';
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  sortBy?: 'orderedAt' | 'total' | 'status';
  sortOrder?: 'asc' | 'desc';
}

export interface OrderListResponse {
  items: VendorOrder[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface OrderStatusUpdate {
  status: OrderStatus;
  trackingNumber?: string;
  vendorNotes?: string;
}

// =============================================================================
// Reviews
// =============================================================================

export interface VendorReview {
  id: string;
  productId: string;
  productName: string;
  productImage: string | null;
  userId: string;
  userName: string;
  userAvatar: string | null;
  rating: number;
  title: string;
  content: string;
  pros: string[];
  cons: string[];
  verifiedPurchase: boolean;
  helpfulCount: number;
  images: string[];
  vendorResponse: string | null;
  vendorRespondedAt: string | null;
  status: ReviewStatus;
  createdAt: string;
}

export interface ReviewListParams {
  page?: number;
  pageSize?: number;
  rating?: number | 'all';
  hasResponse?: boolean;
  productId?: string;
  sortBy?: 'createdAt' | 'rating' | 'helpful';
  sortOrder?: 'asc' | 'desc';
}

export interface ReviewListResponse {
  items: VendorReview[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  averageRating: number;
  ratingDistribution: Record<number, number>;
}

// =============================================================================
// Analytics
// =============================================================================

export interface AnalyticsTimeframe {
  startDate: string;
  endDate: string;
  granularity: 'day' | 'week' | 'month';
}

export interface ViewsData {
  date: string;
  views: number;
  uniqueViews: number;
}

export interface InquiriesData {
  date: string;
  inquiries: number;
  quoteRequests: number;
}

export interface RevenueData {
  date: string;
  revenue: number;
  orderCount: number;
}

export interface TopProduct {
  productId: string;
  productName: string;
  views: number;
  inquiries: number;
  orders: number;
  revenue: number;
}

export interface VendorAnalytics {
  timeframe: AnalyticsTimeframe;
  summary: {
    totalViews: number;
    viewsChange: number;
    totalInquiries: number;
    inquiriesChange: number;
    totalOrders: number;
    ordersChange: number;
    totalRevenue: number;
    revenueChange: number;
    averageRating: number;
    ratingChange: number;
    responseRate: number;
    responseRateChange: number;
  };
  viewsOverTime: ViewsData[];
  inquiriesOverTime: InquiriesData[];
  revenueOverTime: RevenueData[];
  topProducts: TopProduct[];
  ratingDistribution: Record<number, number>;
}

// =============================================================================
// Quick Stats (Dashboard)
// =============================================================================

export interface VendorStats {
  totalProducts: number;
  activeProducts: number;
  pendingOrders: number;
  totalOrders: number;
  pendingReviews: number;
  avgRating: number;
  totalRevenue: number;
  monthlyRevenue: number;
  isVerified: boolean;
  verificationProgress: number;
}

export interface RecentActivity {
  id: string;
  type: 'order' | 'review' | 'inquiry' | 'product';
  title: string;
  description: string;
  timestamp: string;
  link: string;
}

// =============================================================================
// Form Schemas
// =============================================================================

export const vendorProfileSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  description: z.string().min(50, 'Description must be at least 50 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number is required'),
  website: z.string().url('Invalid URL').optional().or(z.literal('')),
  address: z.object({
    street: z.string().min(1, 'Street is required'),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    zipCode: z.string().min(5, 'ZIP code is required'),
    country: z.string().default('USA'),
  }),
  serviceAreaNational: z.boolean(),
  serviceAreaStates: z.array(z.string()),
  yearsInBusiness: z.number().min(0).max(100),
  certifications: z.array(z.string()),
  insuranceAccepted: z.array(z.string()),
  categoryIds: z.array(z.string()).min(1, 'Select at least one category'),
});

export type VendorProfileFormData = z.infer<typeof vendorProfileSchema>;

export const productSchema = z.object({
  name: z.string().min(3, 'Name must be at least 3 characters'),
  shortDescription: z.string().max(500, 'Short description must be under 500 characters'),
  description: z.string().min(50, 'Description must be at least 50 characters'),
  categoryId: z.string().min(1, 'Category is required'),
  priceType: z.enum(['fixed', 'range', 'quote', 'call']),
  priceMin: z.number().nullable(),
  priceMax: z.number().nullable(),
  acceptsInsurance: z.boolean(),
  insuranceNotes: z.string().optional(),
  features: z.array(z.string()),
  specifications: z.array(z.object({
    key: z.string(),
    value: z.string(),
    unit: z.string().optional(),
  })),
  diagnosisIds: z.array(z.string()),
  ageRangeMin: z.number().nullable(),
  ageRangeMax: z.number().nullable(),
  status: z.enum(['draft', 'active', 'out_of_stock', 'discontinued']),
});

export type ProductFormData = z.infer<typeof productSchema>;

export const reviewResponseSchema = z.object({
  response: z.string().min(20, 'Response must be at least 20 characters'),
});

export type ReviewResponseFormData = z.infer<typeof reviewResponseSchema>;

// =============================================================================
// API Response Types
// =============================================================================

export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface ApiError {
  message: string;
  code: string;
  details?: Record<string, string[]>;
}

export interface ImageUploadResponse {
  url: string;
  filename: string;
}
